import "../asset/css/selectSearch.css";
import ReactDOMServer from "react-dom/server";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import moment from "moment";
import MainLayout from "../layout/main";
import {
	createCustomer,
	createRental,
	getCustomers,
	getItem,
	getProduct,
	getRental,
	updateRental,
	updateItem,
	getItemsByProduct,
	getRentalsByItem,
	checkStock
} from "../service/callAPI";
import Schema, { ProductStatus } from "../type/schema";
import { useLoadState } from "../component/hook";
import { toast } from "react-toastify";
import { Button, Col, Form, Row } from "react-bootstrap";
import ValidatorSpan from "../component/validatorSpan";
import { labelStyle } from "../theme/default";
import SelectSearch, {
	NonCreatableSelect,
	Option
} from "../component/selectSearch";
import { remove as removeAccents } from "remove-accents-diacritics";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import ReceiptPage from "./receipt";
import { cacheCustomers, findCustomer1 } from "../service/cache";
import { accessiblePath } from "../lib/misc";
import CreateUserModal, { popup } from "../component/createUserModal";
import { ProductStore } from "../service/db";
import AsyncSelect from "react-select/async";
import { compact } from "lodash";
import { OptionTypeBase } from "react-select";

const BRANCH_NAME = process.env.REACT_APP_SHOP_NAME || "Tiệm Thanh Xuân";

type TProductList = {
	id: string;
	price: number;
	name: string;
	quantity?: number;
}[];
type TLostItemList = string[];
type RentalFormInputs = {
	id: string;
	customerId: string;
	items: Schema.Rental["items"];
	deposit: number;
	totalFee: number;
	discountPercent: number;
	actualPayment: number;
	startDate: string;
	dueDate: string;
	returnDate: string;
	isDone: boolean;
	lostItems: TLostItemList;
	note: string;
	productList: TProductList;
};

document.addEventListener("wheel", function (event) {
	if ((document?.activeElement as any).type === "number") {
		(document?.activeElement as any).blur();
	}
});

export default function RentalEditPage() {
	const { rentalId } = useParams<{ rentalId: string }>();

	return (
		<MainLayout pageTitle="Đơn Thuê" title={`Đơn ${rentalId || "Mới"}`}>
			<RentalForm rentalId={rentalId} />
			<CreateUserModal />
		</MainLayout>
	);
}

const RentalForm = ({ rentalId }: { rentalId: string }) => {
	const [isLoading, setIsLoading] = useState(false);

	const { register, handleSubmit, watch, formState, setValue } =
		useForm<RentalFormInputs>();
	const { isDirty, errors } = formState;
	const onSubmit: SubmitHandler<RentalFormInputs> = async (data) => {
		try {
			setIsLoading(true);
			const converted = await finalConvert(data);
			if (!converted.id) {
				await handleCreateRental(converted);
				return;
			}

			await handleUpdateRental(converted);
		} catch (e: any) {
			const message = e?.response?.data || e.toString();
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	const [currentRental, setRental] = useState<Schema.Rental>();
	useEffect(() => {
		loadState();

		async function loadState() {
			if (!rentalId) return;
			try {
				const data = await getRental(rentalId);
				setRental(data);
				const converted = await initialConvert(data);
				for (let [key, value] of Object.entries(converted)) {
					setValue(key as any, value as any);
				}
			} catch (e: any) {
				const message = e.toString();
				toast.error(message);
				console.error(e);
			}
		}
	}, [rentalId, setValue]);

	const printOrder = async () => {
		const customer: any = await findCustomer1(watch("customerId"));
		const html = ReactDOMServer.renderToString(
			<ReceiptPage
				branchName={BRANCH_NAME}
				customerName={customer?.name}
				customerPhone={customer?.phone}
				products={watch("productList")}
				deposit={watch("deposit")}
				dueDate={watch("dueDate")}
				discountPercent={watch("discountPercent")}
				totalFee={watch("totalFee")}
				startDate={watch("startDate")}
			/>
		);
		const opened = window.open("");
		opened?.document.write(html);
	};

	return (
		<Form
			className="shadow mb-5"
			style={{
				backgroundColor: "white",
				padding: "20px",
				borderRadius: "0.35rem"
			}}
			onSubmit={handleSubmit(onSubmit)}
		>
			<div className="text-info" style={{ textAlign: "right" }}>
				<FontAwesomeIcon
					style={{
						fontSize: 24,
						margin: "auto",
						cursor: "pointer"
					}}
					icon={faPrint}
					title="In đơn hàng"
					onClick={async () => {
						await handleSubmit(onSubmit)();
						await printOrder();
					}}
				/>
			</div>
			<Row>
				<Col sm={6}>
					<Form.Group>
						<Form.Label style={labelStyle}>Khách hàng:</Form.Label>
						<CustomerSearch
							defaultValue={watch("customerId")}
							valueSetter={(value) => {
								setValue("customerId", value, { shouldDirty: true });
							}}
						/>
						{errors.customerId && (
							<ValidatorSpan message={"Không được để trống trường này"} />
						)}
					</Form.Group>

					<Row>
						<Col sm={4}>
							<Form.Group>
								<Form.Label style={labelStyle}>Ngày thuê:</Form.Label>
								<Form.Control
									type="date"
									defaultValue={moment().format("YYYY-MM-DD")}
									{...register("startDate", {
										required: "Không được để trống trường này"
									})}
								/>
								{errors.startDate && (
									<ValidatorSpan message={errors.startDate.message} />
								)}
							</Form.Group>
						</Col>
						<Col sm={4}>
							<Form.Group>
								<Form.Label style={labelStyle}>Ngày hẹn trả:</Form.Label>
								<Form.Control
									type="date"
									{...register("dueDate", {
										required: "Không được để trống trường này"
									})}
								/>
								{errors.dueDate && (
									<ValidatorSpan message={errors.dueDate.message} />
								)}
							</Form.Group>
						</Col>
						<Col sm={4}>
							<Form.Group>
								<Form.Label style={labelStyle}>Đặt cọc:</Form.Label>
								<Form.Control type="number" {...register("deposit")} />
							</Form.Group>
						</Col>
					</Row>

					<Form.Group>
						<Form.Label style={labelStyle}>Ghi chú:</Form.Label>
						<Form.Control as="textarea" {...register("note")} />
					</Form.Group>
				</Col>
				<Col sm={6}>
					<Form.Group>
						<Form.Label style={labelStyle}>Sản phẩm:</Form.Label>
						<ProductSearch
							rental={currentRental!}
							valueSetter={(values) => {
								setValue("productList", values, { shouldDirty: true });
							}}
							currentProductList={watch("productList")}
						/>
						<hr />
						<ProductList
							defaultDiscount={currentRental?.discountPercent || 0}
							products={watch("productList")}
							setValue={setValue}
						/>
					</Form.Group>
				</Col>
			</Row>

			<hr />
			<h4 className="text-info" style={{ fontWeight: "bold" }}>
				Phần Dành Cho Trả Hàng
			</h4>

			<Row>
				<Col sm={6}>
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<Form.Label style={{ ...labelStyle, paddingLeft: 0 }} column sm={8}>
							Đã trả hàng:
						</Form.Label>
						<Form.Check
							type="checkbox"
							{...register("isDone")}
							onChange={() => {
								setValue("returnDate", moment().format("YYYY-MM-DD"), {
									shouldDirty: true
								});
							}}
						/>
					</div>

					<Form.Group>
						<Form.Label style={labelStyle}>Ngày trả:</Form.Label>
						<Form.Control type="date" {...register("returnDate")} />
					</Form.Group>

					<Form.Group>
						<Form.Label style={labelStyle}>Số tiền trả:</Form.Label>
						<Form.Control type="number" {...register("actualPayment")} />
					</Form.Group>

					<Form.Group>
						<Form.Label style={labelStyle}>Hàng bị mất:</Form.Label>
						<LostItemSearch
							rental={currentRental!}
							items={currentRental?.items!}
							valueSetter={(values) => {
								setValue("lostItems", values, { shouldDirty: true });
							}}
						/>
					</Form.Group>
				</Col>
			</Row>

			<div style={{ textAlign: "center" }}>
				<Button
					className="m-2"
					variant="primary"
					type="submit"
					disabled={!isDirty}
				>
					{isLoading ? "Đang lưu..." : "Lưu"}
				</Button>
				<a className="m-2 btn btn-danger" href="/rental">
					Quay Lại
				</a>
			</div>
		</Form>
	);
};

const CustomerSearch = ({
	defaultValue,
	valueSetter
}: {
	defaultValue: string;
	valueSetter: (newValue: any) => void;
}) => {
	return (
		<SelectSearch
			id="customerSearch"
			fetcher={useCallback(async () => {
				const customers = await getCustomers({
					limit: -1,
					page: 1
				});
				return customers.customers.map((c: any) => ({
					value: c.id,
					label: `${c.name} | ${c.phone}`,
					phone: c.phone
				}));
			}, [])}
			defaultValue={defaultValue}
			onChange={(option: any) => {
				valueSetter(option?.value);
			}}
			onCreateOption={async (inputValue) => {
				const newCustomer = await createCustomer({
					name: inputValue
				} as Schema.Customer);
				const newOpt = { label: inputValue, value: newCustomer.id };
				valueSetter(newOpt.value);

				popup(newCustomer.id);
				cacheCustomers();
				return newOpt;
			}}
			filterOption={(option: any, searchText: any) => {
				if (option.data.__isNew__) return true;

				return option.label
					.toLowerCase()
					.includes(removeAccents(searchText.toLowerCase())) ||
					option.data?.phone
						?.replace(/\s/g, "")
						.includes(searchText.replace(/\s/g, ""))
					? true
					: false;
			}}
		/>
	);
};

const ProductSearch = ({
	rental,
	valueSetter,
	currentProductList
}: {
	rental?: Schema.Rental;
	valueSetter: (newValue: TProductList) => void;
	currentProductList?: TProductList;
}) => {
	const productsToOptions = useCallback(async (products: Schema.Product[]) => {
		return products.map((p) => ({
			value: p.id,
			price: p.price,
			label: (
				<div
					style={{
						display: "flex",
						flexWrap: "nowrap",
						justifyContent: "space-between"
					}}
				>
					<div>
						<img
							style={{ marginRight: 8, width: 180 }}
							src={accessiblePath(p.thumbnailUrl)}
							alt="Hình"
							loading="lazy"
						/>
						<span>{p.name}</span>
					</div>
					<span style={{ alignSelf: "center", marginLeft: 4 }}>
						| Kho: {p.stock}
					</span>
				</div>
			),
			searchName: removeAccents(p.name.toLowerCase()),
			name: p.name,
			sku: p.sku || 0
		}));
	}, []);

	const loadOptions = useCallback(
		async (searchText) => {
			const products: Schema.Product[] = await ProductStore.fullTextSearch(
				searchText
			);
			return productsToOptions(products);
		},
		[productsToOptions]
	);

	const findOptionsByValue = useCallback(
		(values: string[], options: OptionTypeBase[]): OptionTypeBase[] => {
			const findOption = (value: string, options: OptionTypeBase[]) => {
				let option: OptionTypeBase;
				for (let o of options) {
					if (value === o.value) {
						option = o;
						break;
					}
				}

				return option!;
			};

			const result = [];
			for (let each of values) {
				const option = findOption(each, options);
				result.push(option);
			}
			return compact(result);
		},
		[]
	);

	const [values, setValues] = useState<OptionTypeBase>();
	const [defaultOptions, setDefaultOptions] = useState<OptionTypeBase[]>();

	useLoadState(
		useCallback(async () => {
			const defaultValues = await itemsToProductList(rental?.items!).then(
				(productList) => productList.map((p) => p.id)
			);
			const products = await ProductStore.getItems();
			const allOptions = await productsToOptions(products);

			const defaults = findOptionsByValue(defaultValues, allOptions);
			setDefaultOptions(defaults);
			setValues(defaults);
		}, [findOptionsByValue, productsToOptions, rental]),
		setDefaultOptions
	);

	return (
		<AsyncSelect
			isMulti
			defaultOptions={defaultOptions}
			loadOptions={loadOptions}
			onChange={async (options) => {
				try {
					const converted = options.map((v) => ({
						id: v.value,
						price: v.price,
						name: v.name,
						quantity:
							currentProductList?.find((p) => p.id === v.value)?.quantity || 1
					}));
					if (rental && !rental.isDone) {
						throw new Error("Không thể thay đổi sản phẩm khi Chưa Trả Hàng");
					}

					for (const p of converted) {
						const response = await checkStock(p.id);
						const stock = response?.stock;
						if (+stock <= 0) {
							toast.warning(`${p.name} không đủ kho, hãy kiểm tra kỹ`);
						}
					}

					valueSetter(converted);
					setValues(options);
				} catch (e: any) {
					const message = e?.response?.data || e.toString();
					toast.error(message);
				}
			}}
			value={values}
		/>
	);
};

const ProductList = ({
	products,
	defaultDiscount,
	setValue
}: {
	products: { id: string; price: number; name: string; quantity?: number }[];
	defaultDiscount: number;
	setValue: Function;
}) => {
	const updateProduct = useCallback(
		(id: string, price: number, quantity: number) => {
			const updated = [...products];

			for (let p of updated) {
				if (p.id === id) {
					p.price = price;
					p.quantity = quantity || 1;
					break;
				}
			}
			setValue("productList", updated, { shouldDirty: true });
		},
		[products, setValue]
	);

	const [displayPercent, setDisplayPercent] = useState(0);
	useEffect(() => {
		setDisplayPercent(defaultDiscount * 100);
	}, [defaultDiscount]);

	let totalPrice =
		products && products.reduce((a, b) => a + b.price * (b.quantity || 1), 0);
	totalPrice -= totalPrice * (displayPercent / 100);
	setValue("totalFee", totalPrice);

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					marginBottom: 8
				}}
			>
				<div></div>
				<div className="d-flex">
					<div style={{ width: 120, textAlign: "right", paddingRight: "12px" }}>
						Đơn giá
					</div>
					<div style={{ width: 120, textAlign: "right", paddingRight: "4px" }}>
						Số lượng
					</div>
				</div>
			</div>

			{products &&
				products.map((p) => (
					<ProductRow key={p.id} product={p} onChange={updateProduct} />
				))}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					marginBottom: 8
				}}
			>
				<i>Chiết khấu (%)</i>
				<Form.Control
					type="number"
					value={displayPercent}
					onChange={(e) => {
						const value = +e?.target?.value;
						setDisplayPercent(value);
						setValue("discountPercent", value / 100, { shouldDirty: true });
					}}
					style={{ width: 120, textAlign: "right", fontStyle: "italic" }}
				/>
			</div>

			{totalPrice > 0 && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						marginBottom: 8
					}}
				>
					<b>Thành tiền</b>
					<Form.Control
						type="number"
						value={totalPrice}
						readOnly
						style={{ width: 120, textAlign: "right", fontWeight: "bold" }}
					/>
				</div>
			)}
		</>
	);
};

const ProductRow = ({
	product,
	onChange
}: {
	product: { id: string; price: number; name: string; quantity?: number };
	onChange: Function;
}) => {
	const [value, setValue] = useState(0);
	const [quantity, setQuantity] = useState(product.quantity || 1);

	useEffect(() => {
		setValue(product.price);
	}, [product.price]);

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				marginBottom: 8
			}}
		>
			<div>{product.name}</div>
			<div className="d-flex">
				<Form.Control
					style={{ width: 120, textAlign: "right" }}
					type="number"
					min={0}
					className="itemPrices mx-2"
					value={value}
					onChange={(e) => {
						const newValue = +e.target.value;
						setValue(newValue);
						onChange(product.id, newValue, quantity);
					}}
				/>

				<Form.Control
					style={{ width: 120, textAlign: "right" }}
					type="number"
					min={1}
					className="itemPrices"
					value={quantity}
					onChange={(e) => {
						const newQuantity = +e.target.value;
						setQuantity(newQuantity);
						onChange(product.id, value, newQuantity);
					}}
				/>
			</div>
		</div>
	);
};

const LostItemSearch = ({
	rental,
	items,
	valueSetter
}: {
	rental: Schema.Rental;
	items: { itemId: string; price: number }[];
	valueSetter: (newValue: TLostItemList) => void;
}) => {
	return (
		<NonCreatableSelect
			isMulti
			fetcher={useCallback(async () => {
				if (!items || !Array.isArray(items)) {
					return [];
				}

				const options = [];
				for (let item of items) {
					const product = await itemIdToProduct(item.itemId);
					if (!product) {
						continue;
					}
					options.push({ label: product?.name, value: item.itemId });
				}

				return options;
			}, [items])}
			defaultValue={rental?.lostItems}
			onChange={(options) => {
				const converted = options.map((v: Option) => v.value);
				valueSetter(converted);
			}}
		/>
	);
};

async function initialConvert(data: Schema.Rental): Promise<RentalFormInputs> {
	const startDate = data.startDate
		? moment.unix(+data.startDate).format("YYYY-MM-DD")
		: moment().format("YYYY-MM-DD");
	const dueDate =
		data.dueDate && moment.unix(+data.dueDate).format("YYYY-MM-DD");
	const returnDate =
		data.returnDate && moment.unix(+data.returnDate).format("YYYY-MM-DD");
	const productList = await itemsToProductList(data.items);

	return {
		...data,
		startDate,
		dueDate,
		returnDate,
		productList
	} as unknown as RentalFormInputs;
}

async function finalConvert(data: RentalFormInputs): Promise<Schema.Rental> {
	const startDate = +moment(data.startDate).format("X");
	const dueDate = +moment(data.dueDate).format("X");
	const returnDate = data.returnDate && +moment(data.returnDate).format("X");
	let items: Schema.Rental["items"] = [];

	await makeCurrentItemsAvailable(data.items || []);
	for (let p of data.productList) {
		const selected = await selectItems(p.id, p.quantity);

		if (!selected) {
			throw new Error(`${p.name} không sẵn kho`);
		}

		items.push(
			...selected.map((i) => ({
				itemId: i.id,
				price: p.price
			}))
		);
	}

	return {
		...data,
		startDate,
		dueDate,
		returnDate,
		items
	} as unknown as Schema.Rental;
}

async function selectItems(productId: string, quantity?: number) {
	return getAvailableItems(productId, quantity);
}

async function makeCurrentItemsAvailable(currentItems: Schema.Rental["items"]) {
	for (let item of currentItems) {
		await updateItem({
			id: item.itemId,
			status: ProductStatus.AVAILABLE
		} as Schema.Item);
	}
}

async function getAvailableItems(
	productId: string,
	quantity?: number
): Promise<Schema.Item[]> {
	const items = await getItemsByProduct(productId);

	const availables = items.filter(
		(i: Schema.Item) => i.status === ProductStatus.AVAILABLE
	);

	if (!quantity) {
		throw new Error("Kiểm tra lại số lượng");
	}
	if (availables.length >= quantity) {
		return availables.slice(0, quantity);
	}
	if (availables.length < quantity) {
		throw new Error("Số lượng không đủ");
	}
	return [];
}

async function getAvailableItem(
	productId: string
): Promise<Schema.Item | void> {
	const items = await getItemsByProduct(productId);
	const availables = items.filter(
		(i: Schema.Item) => i.status === ProductStatus.AVAILABLE
	);
	if (availables.length > 0) {
		return availables[0];
	}
}

async function handleCreateRental(payload: Schema.Rental): Promise<void> {
	const resp = await createRental(payload);
	await updateItemStatus(payload);
	// toast.info("Tạo đơn thành công");
	setTimeout(() => (window.location.href = `/rental/${resp.id}`), 200);
}

async function handleUpdateRental(payload: Schema.Rental): Promise<void> {
	const resp = await updateRental(payload);
	await updateItemStatus(payload);
	// toast.info("Cập nhật đơn thành công");
	setTimeout(() => (window.location.href = `/rental/${resp.id}`), 200);
}

export async function itemsToProductList(
	items: Schema.Rental["items"]
): Promise<TProductList> {
	let productList: TProductList = [];
	if (items && Array.isArray(items)) {
		const products: {
			id: string;
			price: number;
			name: string;
			quantity?: number;
		}[] = [];

		for (let item of items) {
			const product = await itemIdToProduct(item.itemId);
			if (!product) {
				continue;
			}

			if (!products.find((p) => p.id === product.id)) {
				products.push({
					id: product.id,
					price: item.price,
					name: product.name,
					quantity: 1
				});
			} else {
				const p = products.find((p) => p.id === product.id);
				if (p) {
					p.quantity = p.quantity ? p.quantity + 1 : 2;
				}
			}
		}
		productList = products;
	}
	return productList;
}

async function itemIdToProduct(
	itemId: string
): Promise<Schema.Product | undefined> {
	try {
		const fullItem = await getItem(itemId);
		const product = await getProduct(fullItem.productId);
		return product;
	} catch (e: any) {
		const message = e?.response?.data || e.toString();
		toast.error(message);
	}
}

async function updateItemStatus(rental: Schema.Rental) {
	for (const item of rental.items) {
		const itemIsInMoreThanOneActiveRental: boolean =
			await isItemInMoreThanOneActiveRental(item.itemId);

		let status = ProductStatus.IN_RENT;
		if (rental?.lostItems?.includes(item.itemId)) {
			status = ProductStatus.LOST;
		} else if (rental.isDone && !itemIsInMoreThanOneActiveRental) {
			status = ProductStatus.AVAILABLE;
		}
		if (status != null) {
			await updateItem({ id: item.itemId, status } as Schema.Item);
		}
	}
}

async function isItemInMoreThanOneActiveRental(
	itemId: string
): Promise<boolean> {
	const rentals = await getRentalsByItem(itemId);
	const activeRentals = rentals.rentals.filter((rental) => !rental.isDone);

	return activeRentals.length > 0;
}
